import type { FunctionComponent, ReactElement } from 'react';
import { type ProjectsTextBannerProps } from '../../project-components/projects-text-banner/projects-text-banner.component';
import { type ColorTextProps } from './color-text/color-text.component';
import { type ConfigurationOptionsProps } from './configuration-options/configuration-options.component';
import { type DeliveryProps } from './delivery/delivery.component';
import { type FinishProps } from './finish/finish.component';
import { type ModelProps } from './model/model.component';
import { type NonstockBadgeProps } from './nonstock-badge/nonstock-badge.component';
import { type PriceProps } from './price/price.component';
import { removeLastMargin } from './product-line-items.css';
import { type QuantityProps } from './quantity/quantity.component';
import { type RebateWidgetProps } from './rebate-widget/rebate-widget.component';
import { type ReviewStarRatingProps } from './review-star-rating/review-star-rating.component';
import { type ShippingProps } from './shipping/shipping.component';
import { type SourceLabelProps } from './source-label/source-label.component';
import { type StockProps } from './stock/stock.component';
import { type SwatchesProps } from './swatches/swatches.component';
import { type TitleProps } from './title/title.component';

export type ProductLineItemsProps = {
	ProjectsTextBanner?: ReactElement<ProjectsTextBannerProps>;
	SourceLabel?: ReactElement<SourceLabelProps>;
	Swatches?: ReactElement<SwatchesProps>;
	ColorText?: ReactElement<ColorTextProps>;
	Title?: ReactElement<TitleProps>;
	Model?: ReactElement<ModelProps>;
	NonstockBadge?: ReactElement<NonstockBadgeProps>;
	Finish?: ReactElement<FinishProps>;
	ConfigurationOptions?: ReactElement<ConfigurationOptionsProps>;
	Stock?: ReactElement<StockProps>;
	ReviewStarRating?: ReactElement<ReviewStarRatingProps>;
	priceOverrideMessage?: string;
	Price?: ReactElement<PriceProps>;
	Quantity?: ReactElement<QuantityProps>;
	RebateWidget?: ReactElement<RebateWidgetProps>;
	selectedWarranty?: string;
	couponApplied?: boolean;
	Shipping?: ReactElement<ShippingProps>;
	Delivery?: ReactElement<DeliveryProps>;
	justifyItemsToEnd?: boolean;
	fixedTitleModelHeight?: boolean;
};

export const ProductLineItems: FunctionComponent<ProductLineItemsProps> = ({
	ProjectsTextBanner,
	SourceLabel,
	Swatches,
	ColorText,
	Title,
	Model,
	NonstockBadge,
	Finish,
	ConfigurationOptions,
	Stock,
	ReviewStarRating,
	priceOverrideMessage,
	Price,
	Quantity,
	RebateWidget,
	selectedWarranty,
	couponApplied,
	Shipping,
	Delivery,
	justifyItemsToEnd,
	fixedTitleModelHeight
}) => (
	<div data-testid="product-line-items" className={`${removeLastMargin} mw-100 ${justifyItemsToEnd ? 'flex flex-column h-100' : ''}`}>
		{ProjectsTextBanner && (
			<div data-testid="product-text-banner" className="mb1 flex flex-start">
				{ProjectsTextBanner}
			</div>
		)}
		{SourceLabel && (
			<div data-testid="product-source-label" className="mb1">
				{SourceLabel}
			</div>
		)}
		{(Swatches || ColorText) && (
			<div className="mb1">
				<div data-testid="product-swatches">{Swatches}</div>
				<div data-testid="product-color-text">{ColorText}</div>
			</div>
		)}
		<div style={fixedTitleModelHeight ? { height: '5.4rem', overflow: 'hidden' } : {}}>
			{Title && (
				<div data-testid="product-title" className="mb1">
					{Title}
				</div>
			)}
			{Model && (
				<div data-testid="product-model" className="mb1">
					{Model}
				</div>
			)}
		</div>
		{NonstockBadge && (
			<div data-testid="product-nonstock-badge" className="mb1">
				{NonstockBadge}
			</div>
		)}
		<div className={justifyItemsToEnd ? 'flex flex-column justify-end h-100' : ''}>
			<div className={removeLastMargin}>
				{Finish && (
					<div data-testid="product-finish" className="mb1">
						{Finish}
					</div>
				)}
				{ConfigurationOptions && (
					<div data-testid="product-configuration-options" className="mb1">
						{ConfigurationOptions}
					</div>
				)}
				{Stock && (
					<div data-testid="product-stock" className="mb1">
						{Stock}
					</div>
				)}
				{ReviewStarRating && (
					<div data-testid="product-review-star-rating" className="mb1">
						{ReviewStarRating}
					</div>
				)}
				{priceOverrideMessage && (
					<div data-testid="product-price-override-message" className="f6 b mb1">
						{priceOverrideMessage}
					</div>
				)}
				{Price && (
					<div data-testid="product-price" className={Quantity ? '' : 'mb1'}>
						{Price}
					</div>
				)}
				{Quantity && (
					<div data-testid="product-quantity" className="mb1">
						{Quantity}
					</div>
				)}
				{/* RebateWidget conditionally renders internally - bottom margin should be added where implemented */}
				{RebateWidget && <div data-testid="product-rebate-widget">{RebateWidget}</div>}
				{selectedWarranty && (
					<div data-testid="product-selected-warranty" className="f6 mb1">
						{selectedWarranty}
					</div>
				)}
				{couponApplied && (
					<div data-testid="product-coupon-applied" className="f6 b mb1 theme-error">
						Coupon Applied
					</div>
				)}
				{Delivery && (
					<div data-testid="product-delivery" className="mb1">
						{Delivery}
					</div>
				)}
				{Shipping && (
					<div data-testid="product-shipping" className="mb1">
						{Shipping}
					</div>
				)}
			</div>
		</div>
	</div>
);
